import { fetcher } from '#lib/backendFetcher';

import { LEAD_FILTER_PARAM_KEYS } from '../constants/leadFilters';

/**
 * Appends only the set filter keys, so URLs stay clean without active filters.
 * @param {URLSearchParams} params
 * @param {Object} filters - Active filters (see LEAD_FILTER_PARAM_KEYS)
 * @returns {URLSearchParams} The same instance, for chaining
 */
const appendLeadFilterParams = (params, filters) => {
	for (const key of LEAD_FILTER_PARAM_KEYS) {
		if (filters[key] !== undefined) params.set(key, String(filters[key]));
	}
	return params;
};

/**
 * Fetches leads for a search query with pagination + server-side sorting and
 * filtering and transforms the data.
 * @param {string} searchQueryId - UUID of the search query
 * @param {Object} [options]
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.pageSize=100] - Entries per page
 * @param {string} [options.orderBy='created-at'] - Sort key (name|rating|created-at|revenue|employees|ai-score|contacts)
 * @param {string} [options.orderDir='desc'] - Sort direction (asc|desc)
 * @param {Object} [options.filters={}] - Active filters (see LEAD_FILTER_PARAM_KEYS); only set keys are sent, count reflects the filtered total
 * @returns {Promise<{data: Array<Object>, error: null, count: number, hasMore: boolean, page: number, pageSize: number, totalPages: number}>}
 */
export const fetchLeadsBySearchQueryId = async (
	searchQueryId,
	{ page = 1, pageSize = 100, orderBy = 'created-at', orderDir = 'desc', filters = {} } = {},
) => {
	const params = appendLeadFilterParams(new URLSearchParams({ page, pageSize, orderBy, orderDir }), filters);
	const result = await fetcher(`/api/v1/leads/queries/${searchQueryId}/companies?${params}`);

	// Backend flattens globalCompanyEntries to top level and attaches the latest financial
	// KPIs, aiScore, and contactCount flat on each entry.
	const companies = (result.companies ?? []).map((entry) => ({
		...entry,
		prospectQueryId: searchQueryId,
		globalCompanyEntries: {
			companyStatus: entry.companyStatus,
			legalForm: entry.legalForm,
			registerAuthority: entry.registerAuthority,
			websiteUrl: entry.websiteUrl,
			domain: entry.domain,
			rating: entry.rating,
			reviewCount: entry.reviewCount,
		},
	}));
	const transformedData = companies;

	const total = result.total ?? 0;

	return {
		data: transformedData,
		error: null,
		count: total,
		hasMore: page * pageSize < total,
		page,
		pageSize,
		totalPages: total ? Math.ceil(total / pageSize) : 0,
	};
};

/**
 * Sends leads to the enrichment queue for contact search.
 * @param {Object} params
 * @param {{leadIds: string[]} | {selectAll: true, filters?: Object}} params.scope - Companies to enrich (see useActionScope)
 * @param {string} params.prospectQueryId - Search query the enrichment is started from
 * @param {Array<string>} [params.contactRoles] - Desired contact roles in priority order
 * @param {number} [params.maxContacts] - Maximum number of contacts per lead (1-50)
 * @param {Array<'phone'>} [params.preferredChannels] - Rank contacts with a number first and reveal it (phone add-on)
 * @returns {Promise<{jobId: string, queue: string}>}
 */
export const addLeadsToEnrichmentQueue = async ({
	scope,
	prospectQueryId,
	contactRoles = null,
	maxContacts = null,
	preferredChannels = null,
}) => {
	const body = { ...scope, prospectQueryId };

	if (contactRoles?.length > 0) {
		body.contactRoles = contactRoles;
	}

	if (maxContacts !== null && maxContacts !== undefined) {
		body.maxContacts = maxContacts;
	}

	if (preferredChannels?.length > 0) {
		body.preferredChannels = preferredChannels;
	}

	const { jobId } = await fetcher('/api/v1/leads/enrichment/bulk', {
		method: 'POST',
		body,
	});

	return { jobId, queue: 'contactEnrichment' };
};

/**
 * Aborts a running contact enrichment job.
 * @param {string} jobId - Bull job ID
 * @returns {Promise<Object>}
 */
export const abortContactEnrichmentJob = async (jobId) => {
	return await fetcher(`/api/v1/leads/enrichment/bulk/abortJob/${jobId}`, {
		method: 'POST',
	});
};

/**
 * Deletes leads from a search query (soft-exclude, including their contacts).
 * @param {Object} params
 * @param {string} params.prospectQueryId
 * @param {{leadIds: string[]} | {selectAll: true, filters?: Object}} params.scope - Companies to delete (see useActionScope)
 * @returns {Promise<{excluded: number}>} Number of companies removed from the query
 */
export const deleteLeads = ({ prospectQueryId, scope }) =>
	fetcher(`/api/v1/leads/queries/${prospectQueryId}/companies/bulk-exclude`, {
		method: 'POST',
		body: scope,
	});
